import React, { useState, useMemo } from 'react';
import ProductCard from './ProductCard';
import './ImageSearchResults.css';
import { useData } from '../context/DataContext';
import { groupBrowsableProductsForGrid, groupProductsForGrid } from '../utils/productOffers';

const matchTierMessages = {
  in_catalog: 'Matched in our catalog',
  similar: 'Close matches — browse similar listings below',
  category_suggestions: 'No close match — here are products in the same category you may like',
  visual_similarity: 'Visually similar listings',
  visual_text_blend: 'Matched using visual search and product details',
  none: 'No confident match in our catalog',
  unknown: 'Search results',
};

const ImageSearchResults = ({
  imageData,
  searchQuery,
  onAddToList,
  onAddToCart,
  onAddToCompare,
  onCheckAvailability,
  onRemoveFromList,
  listProductIds,
  compareProductIds,
  onClear,
  onRefineSearch,
  analysisData,
  matchingProducts,
  isAnalyzing = false,
}) => {
  const handleAddToList = onAddToList || onAddToCart;
  const { products, categories, loading: dataLoading, searchProducts, getAvailableProducts } = useData();
  const [displayCount, setDisplayCount] = useState(12);

  const matchTier = analysisData?.matchTier || analysisData?.data?.matchTier || 'unknown';

  // Use matching products from backend; when backend returns 0, try similar from catalog (e.g. same category/keyword)
  const filteredProducts = useMemo(() => {
    if (Array.isArray(matchingProducts) && matchingProducts.length > 0) {
      return getAvailableProducts(matchingProducts);
    }
    const tier = analysisData?.matchTier || analysisData?.data?.matchTier;
    if (tier === 'none' && Array.isArray(matchingProducts) && matchingProducts.length === 0) {
      return [];
    }
    if (Array.isArray(matchingProducts) && matchingProducts.length === 0 && analysisData?.data) {
      const d = analysisData.data;
      const terms = [d.productName, d.category, (d.keywords && d.keywords[0])].filter(Boolean);
      for (const term of terms) {
        const similar = searchProducts(term);
        if (similar.length > 0) return getAvailableProducts(similar);
      }
    }
    if (!searchQuery || searchQuery === 'product') {
      return getAvailableProducts(products);
    }
    const searched = searchProducts(searchQuery);
    return getAvailableProducts(searched);
  }, [searchQuery, products, searchProducts, getAvailableProducts, matchingProducts, analysisData, matchTier]);

  // Fallback: products in the same category when we have no direct matches (for "Products in [category]" section)
  const aiCategory = (analysisData?.data?.category || '').trim().toLowerCase();
  const fallbackByCategory = useMemo(() => {
    if (!aiCategory || filteredProducts.length > 0) return [];
    return getAvailableProducts(products).filter(p => {
      const name = (p.categoryName || '').toLowerCase();
      const slug = (p.categorySlug || (p.categoryName || '').toLowerCase().replace(/\s+/g, '-'));
      return name.includes(aiCategory) || slug.includes(aiCategory) || aiCategory.includes(name) || aiCategory.includes(slug.split('-')[0]);
    });
  }, [products, aiCategory, filteredProducts.length, getAvailableProducts]);
  const categoryDisplayName = useMemo(() => {
    if (!aiCategory) return '';
    const found = (categories || []).find(c => 
      (c.name || '').toLowerCase().includes(aiCategory) || (c.slug || '').toLowerCase().includes(aiCategory)
    );
    return found ? found.name : aiCategory.charAt(0).toUpperCase() + aiCategory.slice(1);
  }, [categories, aiCategory]);

  const catalogProducts = useMemo(
    () => getAvailableProducts(products),
    [products, getAvailableProducts]
  );

  const groupedProducts = useMemo(() => {
    const withImages = filteredProducts.map((p) => ({
      ...p,
      image: p.images && p.images.length > 0 ? p.images[0] : (p.image || '/images/products/electronics.jpg')
    }));
    return groupBrowsableProductsForGrid(withImages, catalogProducts);
  }, [filteredProducts, catalogProducts]);

  const handleShowMore = () => {
    setDisplayCount(prev => prev + 12);
  };

  const handleRequestProduct = () => {
    const requestData = analysisData?.data || {};
    let description = requestData.description || '';
    if (!description && requestData.productName) {
      description = `Product identified from image: ${requestData.productName}`;
      if (requestData.brand) description += ` (${requestData.brand})`;
      if (requestData.attributes && typeof requestData.attributes === 'object') {
        const attrs = Object.entries(requestData.attributes)
          .filter(([, v]) => v)
          .map(([k, v]) => `${k}: ${v}`)
          .join(', ');
        if (attrs) description += `. Attributes: ${attrs}`;
      }
    }
    const productName = requestData.productName || searchQuery || 'Product';
    const waNumber = '254708177021'; // Same WhatsApp contact used across Raheasy
    const lines = [
      'Hi Raheasy, I would like to request a product.',
      '',
      `Name: ${productName}`,
      requestData.brand ? `Brand: ${requestData.brand}` : '',
      requestData.category ? `Category: ${requestData.category}` : '',
      description ? `Details: ${description}` : '',
    ].filter(Boolean);
    const text = encodeURIComponent(lines.join('\n'));
    const url = `https://wa.me/${waNumber}?text=${text}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const displayedProducts = groupedProducts.slice(0, displayCount);
  const hasMore = groupedProducts.length > displayCount;

  const analysisComplete = matchingProducts !== null;
  const hasBackendMatches = Array.isArray(matchingProducts) && matchingProducts.length > 0;

  if (isAnalyzing || (imageData && !analysisComplete)) {
    return (
      <div className="image-search-results image-search-results--in-column">
        <div className="image-search-header">
          <div className="search-image-preview-container">
            <img src={imageData} alt="Search" className="search-image-preview" />
            <button className="clear-image-search" onClick={onClear} title="Clear image search">
              ×
            </button>
          </div>
          <div className="search-info">
            <h2>Analyzing your image…</h2>
            <p>Matching against our catalog. This usually takes a few seconds.</p>
          </div>
        </div>
        <div className="loading-products">
          <div className="loading-spinner"></div>
          <p>Finding visually similar products</p>
        </div>
      </div>
    );
  }

  if (dataLoading && products.length === 0 && !hasBackendMatches) {
    return (
      <div className="image-search-results image-search-results--in-column">
        <div className="image-search-header">
          <div className="search-image-preview-container">
            <img src={imageData} alt="Search" className="search-image-preview" />
            <button className="clear-image-search" onClick={onClear} title="Clear image search">
              ×
            </button>
          </div>
          <div className="search-info">
            <h2>Searching for similar products...</h2>
            <p>Analyzing your image...</p>
          </div>
        </div>
        <div className="loading-products">
          <div className="loading-spinner"></div>
          <p>Finding products that match your image</p>
        </div>
      </div>
    );
  }

  const aiProductName = analysisData?.data?.productName || '';
  const aiCategoryLabel = analysisData?.data?.category ? (categoryDisplayName || analysisData.data.category) : '';
  const aiSubCategory = (analysisData?.data?.subCategory || '').trim();
  const aiBrand = (analysisData?.data?.brand || '').trim();
  const tierHint = matchTierMessages[matchTier] || matchTierMessages.unknown;

  return (
    <div className="image-search-results image-search-results--in-column">
      <div className="image-search-header">
        <div className="search-image-preview-container">
          <img src={imageData} alt="Search" className="search-image-preview" />
          <button className="clear-image-search" onClick={onClear} title="Clear image search">
            ×
          </button>
        </div>
        <div className="search-info">
          <p className="image-search-tier-hint" role="status">{tierHint}</p>
          {analysisData?.voyageWeakMatch && matchTier !== 'in_catalog' && (
            <p className="image-search-confidence-hint">
              We could not find a strong visual match — showing the closest options we found.
            </p>
          )}

          {aiProductName && (
            <div className="image-search-ai-description">
              <p className="image-search-we-think">We think this is: <strong>{aiProductName}</strong></p>
              {aiCategoryLabel && <p className="image-search-category">Category (from our list): {aiCategoryLabel}</p>}
              {aiSubCategory && <p className="image-search-subcategory">Subcategory: {aiSubCategory}</p>}
              {aiBrand && <p className="image-search-brand">Brand identified: {aiBrand}</p>}
              {onRefineSearch && (
                <div className="image-search-refine-actions">
                  <button type="button" className="btn-search-similar" onClick={() => onRefineSearch(aiProductName)}>
                    Search for similar
                  </button>
                  <button type="button" className="btn-refine" onClick={() => onRefineSearch(aiProductName)}>
                    Refine search
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {groupedProducts.length === 0 ? (
        <>
          <div className="no-results">
            <div className="no-results-content">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="no-results-icon">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="M21 21l-4.35-4.35"></path>
              </svg>
              <h3>No exact match found</h3>
              <p className="hint">
                {aiProductName
                  ? `We identified “${aiProductName}”${aiCategoryLabel ? ` in category “${aiCategoryLabel}”` : ''} but it isn’t listed in our catalog.`
                  : 'We couldn’t match this image to products in our catalog.'}
              </p>
              {aiCategoryLabel && matchTier === 'none' && (
                <p className="hint">You can browse that category from the menu, or request this item below.</p>
              )}
              <p className="hint">
                <strong>Request it:</strong> Chat with us on WhatsApp or use the button below — we may be able to source it for you.
              </p>
              <div className="no-results-actions">
                <button 
                  className="btn-request-product"
                  onClick={() => handleRequestProduct()}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="12" y1="18" x2="12" y2="12"></line>
                    <line x1="9" y1="15" x2="15" y2="15"></line>
                  </svg>
                  Request This Product
                </button>
                <button 
                  className="btn-clear-search"
                  onClick={onClear}
                >
                  Try Another Search
                </button>
                {aiProductName && onRefineSearch && (
                  <button type="button" className="btn-search-similar btn-search-similar-inline" onClick={() => onRefineSearch(aiProductName)}>
                    Search for similar
                  </button>
                )}
              </div>
            </div>
          </div>

          {fallbackByCategory.length > 0 && matchTier !== 'category_suggestions' && (
            <div className="image-search-fallback-section">
              <h3 className="image-search-fallback-title">Products in {categoryDisplayName || aiCategoryLabel || 'this category'}</h3>
              <p className="image-search-fallback-subtitle">Nothing close in name search — you might still find something similar here</p>
              <div className="products-grid">
                {fallbackByCategory.slice(0, 12).map(product => {
                  const p = { ...product, image: product.images?.[0] || product.image || '/images/products/electronics.jpg' };
                  const grouped = groupBrowsableProductsForGrid([p], catalogProducts);
                  const card = grouped[0] || p;
                  return (
                    <ProductCard
                      key={card.offerGroup?.key || product.id || product._id}
                      product={card}
                      onAddToList={handleAddToList}
                      onAddToCompare={onAddToCompare}
                      onCheckAvailability={onCheckAvailability}
                      onRemoveFromList={onRemoveFromList}
                      listProductIds={listProductIds}
                      compareProductIds={compareProductIds}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="products-grid">
            {displayedProducts.map((product) => (
              <ProductCard 
                key={product.offerGroup?.key || product.id || product._id} 
                product={product} 
                onAddToList={handleAddToList}
                onAddToCompare={onAddToCompare}
                onCheckAvailability={onCheckAvailability}
                onRemoveFromList={onRemoveFromList}
                listProductIds={listProductIds}
                compareProductIds={compareProductIds}
              />
            ))}
          </div>
          {hasMore && (
            <div className="show-more-container">
              <button className="show-more-btn" onClick={handleShowMore}>
                Show More Products ({groupedProducts.length - displayCount} remaining)
              </button>
            </div>
          )}
          {analysisData?.data?.productName && (
            <div className="image-search-request-cta">
              <p>Can't find the exact item? Request it and we'll try to source it for you.</p>
              <button type="button" className="btn-request-product btn-request-product-inline" onClick={() => handleRequestProduct()}>
                Request &quot;{analysisData.data.productName}&quot;
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ImageSearchResults;
